import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFitStore } from '../../store/useFitStore';
import { supabase } from '../../lib/supabase';
import { getDefaultUnit } from '../../lib/sets';
import type { Client } from '../../store/types';

/* ============================================================
   ClientPhotos — transformation photos + the case-study builder.

   Photos live in the PRIVATE client-photos bucket (signed URLs,
   never public) with per-photo date/label/weight metadata. The
   case study assembles a before/after pair with stats computed
   from the client's REAL records — weight and body-fat change
   from check-ins, top PR gains from logged sets, session count,
   duration — around an editable headline/story/quote, and
   exports a share-ready PNG.
============================================================ */

interface PhotoRow {
  id: string;
  path: string;
  takenOn: string;
  label: string;
  weightLbs?: number;
  url?: string;
}

const BUCKET = 'client-photos';

// Phones hand us 5-12MB originals; a 1600px JPEG is plenty for cards.
async function downscale(file: File, max = 1600): Promise<Blob> {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, max / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * scale);
  const h = Math.round(bmp.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d')!.drawImage(bmp, 0, 0, w, h);
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', 0.85)
  );
}

const fmtShort = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', year: 'numeric', day: 'numeric' });
};

const PhotosTab: React.FC<{
  client: Client;
  showToast: ReturnType<typeof useFitStore.getState>['showToast'];
}> = ({ client, showToast }) => {
  const { user } = useFitStore();
  const [photos, setPhotos] = useState<PhotoRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [study, setStudy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const refresh = async () => {
    const { data } = await supabase
      .from('client_photos')
      .select('id, storage_path, taken_on, label, weight_lbs')
      .eq('client_id', client.id)
      .order('taken_on', { ascending: true });
    const rows: PhotoRow[] = (data || []).map((r) => ({
      id: r.id, path: r.storage_path, takenOn: r.taken_on, label: r.label || '', weightLbs: r.weight_lbs ?? undefined,
    }));
    if (rows.length > 0) {
      const { data: signed } = await supabase.storage.from(BUCKET).createSignedUrls(rows.map((r) => r.path), 3600);
      (signed || []).forEach((s, i) => { if (s.signedUrl) rows[i].url = s.signedUrl; });
    }
    setPhotos(rows);
    setLoaded(true);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [client.id]);

  const upload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !user) return;
    setUploading(true);
    let ok = 0;
    for (const file of Array.from(files)) {
      try {
        const blob = await downscale(file);
        const path = `${user.id}/${client.id}/${crypto.randomUUID()}.jpg`;
        const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, blob, { contentType: 'image/jpeg' });
        if (upErr) throw upErr;
        const { error: rowErr } = await supabase.from('client_photos').insert({
          id: crypto.randomUUID(), coach_id: user.id, client_id: client.id,
          storage_path: path, taken_on: new Date().toISOString().slice(0, 10), label: '',
        });
        if (rowErr) throw rowErr;
        ok++;
      } catch (e: any) {
        console.error('photo upload', e?.message);
      }
    }
    setUploading(false);
    if (ok > 0) { showToast(`${ok} photo${ok === 1 ? '' : 's'} added`); refresh(); }
    else showToast('Upload failed — try again', 'error');
  };

  const patch = async (id: string, fields: { taken_on?: string; label?: string }) => {
    await supabase.from('client_photos').update(fields).eq('id', id);
    setPhotos((all) => all.map((p) => (p.id === id ? { ...p, takenOn: fields.taken_on ?? p.takenOn, label: fields.label ?? p.label } : p)));
  };

  const remove = async (p: PhotoRow) => {
    await supabase.storage.from(BUCKET).remove([p.path]);
    await supabase.from('client_photos').delete().eq('id', p.id);
    setPhotos((all) => all.filter((x) => x.id !== p.id));
  };

  const first = client.name.split(' ')[0];

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <p className="mono-label">Transformation photos</p>
          <p className="text-[12px] text-fit-muted mt-0.5">Private to you — stored encrypted, never public.</p>
        </div>
        <div className="flex items-center gap-2">
          {photos.length >= 2 && (
            <button onClick={() => setStudy(true)} className="btn btn-primary btn-sm">Create case study →</button>
          )}
          <label htmlFor="client-photo-files" className={`btn btn-glass btn-sm cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
            {uploading ? 'Uploading…' : '＋ Add photos'}
          </label>
        </div>
      </div>
      <input id="client-photo-files" ref={fileRef} type="file" accept="image/*" multiple className="sr-only"
        onChange={(e) => { upload(e.target.files); e.target.value = ''; }} />

      {loaded && photos.length === 0 && (
        <div className="text-center py-14 rounded-fit-md border border-fit-line">
          <p className="text-[14px] font-semibold mb-1">No photos yet</p>
          <p className="text-[12px] text-fit-muted">Add {first}'s progress shots — two photos unlock the case-study builder.</p>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {photos.map((p) => (
          <div key={p.id} className="rounded-fit-md border border-fit-line overflow-hidden">
            <div className="relative aspect-[3/4] bg-fit-bg-tertiary">
              {p.url && <img src={p.url} alt={p.label || 'progress photo'} className="w-full h-full object-cover" />}
              <button
                onClick={() => remove(p)}
                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 text-white/80 hover:text-white text-[12px] leading-none"
                aria-label="Delete photo"
              >
                ×
              </button>
            </div>
            <div className="p-2 flex flex-col gap-1">
              <input
                type="date"
                value={p.takenOn}
                onChange={(e) => patch(p.id, { taken_on: e.target.value })}
                className="bg-transparent outline-none text-[11px] text-fit-text-secondary"
              />
              <input
                value={p.label}
                onChange={(e) => setPhotos((all) => all.map((x) => (x.id === p.id ? { ...x, label: e.target.value } : x)))}
                onBlur={(e) => patch(p.id, { label: e.target.value })}
                placeholder="label — front, side…"
                className="bg-transparent outline-none text-[11px] placeholder:text-fit-muted/50"
              />
            </div>
          </div>
        ))}
      </div>

      {study && <CaseStudy client={client} photos={photos.filter((p) => p.url)} onClose={() => setStudy(false)} showToast={showToast} />}
    </div>
  );
};

/* ---------------- case study builder ---------------- */

const NUMERIC = /^\d+(\.\d+)?$/;

const CaseStudy: React.FC<{
  client: Client;
  photos: PhotoRow[];
  onClose: () => void;
  showToast: ReturnType<typeof useFitStore.getState>['showToast'];
}> = ({ client, photos, onClose, showToast }) => {
  const { checkIns, sessions } = useFitStore();
  const first = client.name.split(' ')[0];
  const unit = getDefaultUnit();

  const [beforeId, setBeforeId] = useState(photos[0]?.id);
  const [afterId, setAfterId] = useState(photos[photos.length - 1]?.id);
  const before = photos.find((p) => p.id === beforeId);
  const after = photos.find((p) => p.id === afterId);

  // Real numbers, computed — the template never asks the coach to do math.
  const stats = useMemo(() => {
    const mine = checkIns
      .filter((c) => c.clientId === client.id)
      .sort((a, b) => a.weekOf.localeCompare(b.weekOf));
    const withW = mine.filter((c) => c.weightLbs != null);
    const withBF = mine.filter((c) => c.bodyFatPct != null);
    const done = sessions
      .filter((s) => s.clientId === client.id && (s.status === 'completed' || s.status === 'partial'))
      .sort((a, b) => a.date.localeCompare(b.date));

    const firstTop: Record<string, { name: string; load: number }> = {};
    const peak: Record<string, number> = {};
    done.forEach((s) =>
      s.sections.forEach((sec) =>
        sec.exercises.forEach((ex) => {
          const loads = ex.sets.map((x) => x.load).filter((l): l is string => !!l && NUMERIC.test(l)).map(Number);
          if (!loads.length) return;
          const top = Math.max(...loads);
          const k = ex.name.trim().toLowerCase();
          if (!(k in firstTop)) firstTop[k] = { name: ex.name.trim(), load: top };
          peak[k] = Math.max(peak[k] ?? 0, top);
        })
      )
    );
    const prs = Object.entries(firstTop)
      .map(([k, f]) => ({ name: f.name, from: f.load, to: peak[k] }))
      .filter((p) => p.to > p.from)
      .sort((a, b) => (b.to - b.from) - (a.to - a.from))
      .slice(0, 3);

    const startIso = client.startDate || done[0]?.date;
    const endIso = done[done.length - 1]?.date || mine[mine.length - 1]?.weekOf;
    const months = startIso && endIso
      ? Math.max(1, Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / (30.44 * 86400000)))
      : undefined;

    return {
      months,
      sessions: done.length,
      weightFrom: withW[0]?.weightLbs, weightTo: withW[withW.length - 1]?.weightLbs,
      bfFrom: withBF[0]?.bodyFatPct, bfTo: withBF[withBF.length - 1]?.bodyFatPct,
      prs,
    };
  }, [checkIns, sessions, client.id, client.startDate]);

  const [headline, setHeadline] = useState(`${first}'s transformation`);
  const [story, setStory] = useState(client.goals ? `The goal: ${client.goals}` : '');
  const [quote, setQuote] = useState('');
  const [exporting, setExporting] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const download = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const { toPng } = await import('html-to-image');
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: '#0a0a0b' });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${client.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-case-study.png`;
      a.click();
      showToast('Case study downloaded');
    } catch (e: any) {
      console.error('export', e?.message);
      showToast('Export failed — try again', 'error');
    }
    setExporting(false);
  };

  const chip = (label: string, value: string) => (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', color: '#fff', margin: 0 }}>{value}</p>
      <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(255,255,255,0.45)', margin: '2px 0 0' }}>{label}</p>
    </div>
  );

  return (
    <div className="modal-overlay" style={{ zIndex: 10020 }} onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 1060 }} onClick={(e) => e.stopPropagation()}>
        <div className="card flex flex-col lg:flex-row gap-6" style={{ maxHeight: '90vh', overflow: 'auto' }}>
          {/* controls */}
          <div className="flex flex-col gap-4 lg:w-[340px] shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold tracking-tight">Case study</h2>
              <button onClick={onClose} className="text-fit-text-secondary hover:text-white text-lg leading-none">×</button>
            </div>

            <div>
              <p className="mono-label mb-1.5">Photos — tap B / A</p>
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                {photos.map((p) => (
                  <div key={p.id} className="shrink-0 w-[72px]">
                    <div className={`relative aspect-[3/4] rounded-md overflow-hidden border ${p.id === beforeId || p.id === afterId ? 'border-white' : 'border-fit-line'}`}>
                      <img src={p.url} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex gap-1 mt-1">
                      <button onClick={() => setBeforeId(p.id)} className={`flex-1 text-[10px] font-bold rounded py-0.5 ${p.id === beforeId ? 'bg-white text-fit-ink' : 'bg-fit-bg-tertiary text-fit-text-secondary'}`}>B</button>
                      <button onClick={() => setAfterId(p.id)} className={`flex-1 text-[10px] font-bold rounded py-0.5 ${p.id === afterId ? 'bg-white text-fit-ink' : 'bg-fit-bg-tertiary text-fit-text-secondary'}`}>A</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="mono-label mb-1.5">Headline</p>
              <input value={headline} onChange={(e) => setHeadline(e.target.value)} className="input w-full" />
            </div>
            <div>
              <p className="mono-label mb-1.5">Story</p>
              <textarea value={story} onChange={(e) => setStory(e.target.value)} placeholder="Where they started, what changed, how it happened…" className="input w-full" style={{ minHeight: 84 }} />
            </div>
            <div>
              <p className="mono-label mb-1.5">Client quote (optional)</p>
              <textarea value={quote} onChange={(e) => setQuote(e.target.value)} placeholder={'"I never thought I\'d…"'} className="input w-full" style={{ minHeight: 56 }} />
            </div>

            <button onClick={download} disabled={exporting || !before || !after} className="btn btn-primary btn-sm">
              {exporting ? 'Rendering…' : 'Download PNG ↓'}
            </button>
            <p className="text-[11px] text-fit-muted">
              Sized for sharing — stats are pulled straight from {first}'s logged data. Get their permission before posting.
            </p>
          </div>

          {/* the card itself — what gets exported */}
          <div className="flex-1 flex items-start justify-center">
            <div
              ref={cardRef}
              style={{
                width: 540, background: '#0a0a0b', color: '#fff', borderRadius: 16,
                border: '1px solid rgba(255,255,255,0.1)', padding: 28,
                fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
              }}
            >
              <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(255,255,255,0.45)', margin: 0 }}>
                Case study{stats.months ? ` · ${stats.months} months` : ''}
              </p>
              <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', margin: '6px 0 18px', lineHeight: 1.1 }}>
                {headline}
              </h1>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[{ p: before, tag: 'Before' }, { p: after, tag: 'After' }].map(({ p, tag }) => (
                  <div key={tag}>
                    <div style={{ position: 'relative', aspectRatio: '3/4', borderRadius: 10, overflow: 'hidden', background: '#16161a' }}>
                      {p?.url && <img src={p.url} crossOrigin="anonymous" alt={tag} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                      <span style={{
                        position: 'absolute', top: 8, left: 8, fontSize: 9, fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.12em',
                        background: 'rgba(0,0,0,0.65)', padding: '3px 8px', borderRadius: 999,
                      }}>
                        {tag}
                      </span>
                    </div>
                    <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', margin: '6px 0 0', textAlign: 'center' }}>
                      {p ? fmtShort(p.takenOn) : '—'}
                    </p>
                  </div>
                ))}
              </div>

              <div style={{
                display: 'flex', justifyContent: 'space-around', gap: 8,
                borderTop: '1px solid rgba(255,255,255,0.1)', borderBottom: '1px solid rgba(255,255,255,0.1)',
                padding: '14px 0', margin: '18px 0',
              }}>
                {stats.weightFrom != null && stats.weightTo != null && chip(`weight ${unit}`, `${stats.weightFrom} → ${stats.weightTo}`)}
                {stats.bfFrom != null && stats.bfTo != null && chip('body fat', `${stats.bfFrom}% → ${stats.bfTo}%`)}
                {stats.sessions > 0 && chip('sessions', String(stats.sessions))}
              </div>

              {stats.prs.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  {stats.prs.map((p) => (
                    <p key={p.name} style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', margin: '3px 0', display: 'flex', justifyContent: 'space-between' }}>
                      <span>{p.name}</span>
                      <span style={{ fontVariantNumeric: 'tabular-nums', color: '#fff', fontWeight: 600 }}>{p.from} → {p.to} {unit}</span>
                    </p>
                  ))}
                </div>
              )}

              {story && <p style={{ fontSize: 13, lineHeight: 1.55, color: 'rgba(255,255,255,0.7)', margin: '0 0 14px' }}>{story}</p>}
              {quote && (
                <p style={{ fontSize: 14, lineHeight: 1.5, fontStyle: 'italic', margin: '0 0 14px' }}>
                  “{quote}” <span style={{ fontStyle: 'normal', fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>— {first}</span>
                </p>
              )}

              <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(255,255,255,0.35)', margin: 0 }}>
                FITFLEX · fitflex.club
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotosTab;
